import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, X, TrendingUp, Users, BarChart2,
  ExternalLink, RefreshCw, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { socialAccountsService } from '../api/socialAccountsApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const PLATFORM_META = {
  instagram: { icon: 'fab fa-instagram', color: 'text-pink-600', bg: 'bg-pink-50' },
  tiktok:    { icon: 'fab fa-tiktok',    color: 'text-gray-800', bg: 'bg-gray-50' },
  linkedin:  { icon: 'fab fa-linkedin',  color: 'text-blue-700', bg: 'bg-blue-50' },
  x:         { icon: 'fab fa-x-twitter', color: 'text-gray-900', bg: 'bg-gray-50' },
  youtube:   { icon: 'fab fa-youtube',   color: 'text-red-600',  bg: 'bg-red-50' },
  facebook:  { icon: 'fab fa-facebook',  color: 'text-blue-600', bg: 'bg-blue-50' },
  other:     { icon: 'fas fa-globe',     color: 'text-gray-500', bg: 'bg-gray-50' },
};

const getPlatformMeta = (platform) =>
  PLATFORM_META[(platform || '').toLowerCase()] || PLATFORM_META.other;

const PLATFORMS = ['instagram', 'tiktok', 'linkedin', 'x', 'youtube', 'facebook', 'other'];

// ── Account Form Modal ────────────────────────────────────────────────
const AccountFormModal = ({ isOpen, onClose, onSave, initial = null }) => {
  const empty = {
    platform: '', handle: '', url: '', displayName: '', description: '',
    brandTone: '', postingCadence: '', managedBy: [], contentPillars: [], preferredFormats: [],
  };
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial || empty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, isOpen]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const csvField = (key, label) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={(form[key] || []).join(', ')}
        onChange={e => set(key, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
        placeholder="Comma-separated values"
        className="w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: SYSTEM_COLORS.borderSoft }}
      />
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {initial ? 'Edit Account' : 'Register Social Account'}
          </h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Platform *</label>
            <select
              required
              value={form.platform}
              onChange={e => set('platform', e.target.value)}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <option value="">Select platform</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>

          {[
            { key: 'handle', label: 'Handle / Username *', required: true, placeholder: '@username' },
            { key: 'displayName', label: 'Display Name', placeholder: 'Lab Social Account' },
            { key: 'url', label: 'Profile URL', placeholder: 'https://...' },
            { key: 'brandTone', label: 'Brand Tone', placeholder: 'Professional, inspiring, …' },
            { key: 'postingCadence', label: 'Posting Cadence', placeholder: '3x per week, daily, …' },
          ].map(({ key, label, required, placeholder }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <input
                type="text"
                required={required}
                value={form[key] || ''}
                onChange={e => set(key, e.target.value)}
                placeholder={placeholder}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ borderColor: SYSTEM_COLORS.borderSoft }}
              />
            </div>
          ))}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={form.description || ''}
              onChange={e => set('description', e.target.value)}
              rows={2}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            />
          </div>

          {csvField('managedBy', 'Managed By (user IDs or names)')}
          {csvField('contentPillars', 'Content Pillars')}
          {csvField('preferredFormats', 'Preferred Formats')}

          {initial && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={form.isActive !== false}
                onChange={e => set('isActive', e.target.checked)}
              />
              <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : initial ? 'Update' : 'Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Snapshot Form Modal ───────────────────────────────────────────────
const SnapshotModal = ({ isOpen, onClose, onSave, accountName }) => {
  const now = new Date().toISOString().slice(0, 16);
  const [form, setForm] = useState({
    captured_at: now, followers: 0, following: 0,
    posts_count: 0, total_views: 0, total_likes: 0, engagement_rate: 1, note: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(f => ({ ...f, captured_at: new Date().toISOString().slice(0, 16) }));
  }, [isOpen]);

  const num = (key, label, step = 1) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input type="number" min={0} step={step}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: parseFloat(e.target.value) || 0 }))}
        className="w-full rounded-lg border px-3 py-2 text-sm"
        style={{ borderColor: SYSTEM_COLORS.borderSoft }}
      />
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, captured_at: new Date(form.captured_at).toISOString() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-2xl border p-6"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            Record Metrics — {accountName}
          </h3>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Captured At</label>
            <input type="datetime-local" value={form.captured_at}
              onChange={e => setForm(f => ({ ...f, captured_at: e.target.value }))}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {num('followers', 'Followers')}
            {num('following', 'Following')}
            {num('posts_count', 'Posts Count')}
            {num('total_views', 'Total Views')}
            {num('total_likes', 'Total Likes')}
            {num('engagement_rate', 'Engagement Rate (%)', 0.1)}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
            <input type="text" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Optional note…"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: SYSTEM_COLORS.borderSoft }}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : 'Save Snapshot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Account Card ──────────────────────────────────────────────────────
const AccountCard = ({ account, onEdit, onDelete, onSnapshot }) => {
  const meta = getPlatformMeta(account.platform);
  const lastHistory = account.metricsHistory?.slice(-1)[0];

  return (
    <div className="rounded-xl border overflow-hidden"
         style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
      {/* Header */}
      <div className={`${meta.bg} flex items-center justify-between px-4 py-3 border-b`}
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex items-center gap-2">
          <i className={`${meta.icon} ${meta.color} text-xl`} />
          <div>
            <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>
              {account.displayName || account.handle}
            </p>
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              @{account.handle} · {account.platform}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {account.isActive
            ? <CheckCircle size={14} className="text-green-500" title="Active" />
            : <AlertCircle size={14} className="text-gray-400" title="Inactive" />
          }
          {account.url && (
            <a href={account.url} target="_blank" rel="noopener noreferrer"
               className="p-1 text-gray-400 hover:text-blue-600 rounded">
              <ExternalLink size={14} />
            </a>
          )}
          <button onClick={() => onSnapshot(account)}
            className="p-1 text-gray-400 hover:text-green-600 rounded" title="Record metrics">
            <TrendingUp size={14} />
          </button>
          <button onClick={() => onEdit(account)}
            className="p-1 text-gray-400 hover:text-blue-600 rounded" title="Edit">
            <Edit2 size={14} />
          </button>
          <button onClick={() => onDelete(account)}
            className="p-1 text-gray-400 hover:text-red-600 rounded" title="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 divide-x text-center py-3"
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="px-3">
          <div className="flex items-center justify-center gap-1 text-xs mb-0.5"
               style={{ color: SYSTEM_COLORS.textMuted }}>
            <Users size={12} /> Followers
          </div>
          <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>
            {(account.followerCount || 0).toLocaleString()}
          </p>
        </div>
        <div className="px-3">
          <div className="flex items-center justify-center gap-1 text-xs mb-0.5"
               style={{ color: SYSTEM_COLORS.textMuted }}>
            <BarChart2 size={12} /> Engagement
          </div>
          <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>
            {((account.engagementRate || 0) * 100).toFixed(1)}%
          </p>
        </div>
        <div className="px-3">
          <div className="flex items-center justify-center gap-1 text-xs mb-0.5"
               style={{ color: SYSTEM_COLORS.textMuted }}>
            <RefreshCw size={12} /> Snapshots
          </div>
          <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>
            {(account.metricsHistory || []).length}
          </p>
        </div>
      </div>

      {/* Meta */}
      {(account.brandTone || account.postingCadence || (account.contentPillars || []).length > 0) && (
        <div className="border-t px-4 py-3 space-y-1"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {account.brandTone && (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              <span className="font-medium">Tone:</span> {account.brandTone}
            </p>
          )}
          {account.postingCadence && (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              <span className="font-medium">Cadence:</span> {account.postingCadence}
            </p>
          )}
          {(account.contentPillars || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {account.contentPillars.map(p => (
                <span key={p}
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
                  {p}
                </span>
              ))}
            </div>
          )}
          {lastHistory && (
            <p className="text-xs pt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>
              Last updated: {new Date(lastHistory.captured_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────
const SocialAccountsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyActive, setOnlyActive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [snapshotTarget, setSnapshotTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await socialAccountsService.list(onlyActive);
      setAccounts(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load social accounts');
    } finally {
      setLoading(false);
    }
  }, [onlyActive]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editingAccount) {
        const updated = await socialAccountsService.update(editingAccount.id, form);
        setAccounts(prev => prev.map(a => a.id === updated.id ? updated : a));
        toast.success('Account updated');
      } else {
        const created = await socialAccountsService.create(form);
        setAccounts(prev => [...prev, created]);
        toast.success('Account registered');
      }
      setEditingAccount(null);
    } catch (err) {
      toast.error(err?.message || 'Save failed');
      throw err;
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Delete @${account.handle}?`)) return;
    try {
      await socialAccountsService.delete(account.id);
      setAccounts(prev => prev.filter(a => a.id !== account.id));
      toast.success('Account deleted');
    } catch {
      toast.error('Failed to delete account');
    }
  };

  const handleSnapshot = async (snapshot) => {
    try {
      const updated = await socialAccountsService.recordSnapshot(snapshotTarget.id, snapshot);
      setAccounts(prev => prev.map(a => a.id === updated.id ? updated : a));
      toast.success('Metrics snapshot saved');
      setSnapshotTarget(null);
    } catch (err) {
      toast.error(err?.message || 'Failed to save snapshot');
      throw err;
    }
  };

  const totalFollowers = accounts.reduce((s, a) => s + (a.followerCount || 0), 0);
  const avgEngagement = accounts.length
    ? accounts.reduce((s, a) => s + (a.engagementRate || 0), 0) / accounts.length
    : 0;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Accounts', value: accounts.length, icon: Users },
          { label: 'Total Followers', value: totalFollowers.toLocaleString(), icon: TrendingUp },
          { label: 'Avg Engagement', value: `${(avgEngagement * 100).toFixed(1)}%`, icon: BarChart2 },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-xl border p-4"
               style={{ backgroundColor: SYSTEM_COLORS.primaryTint, borderColor: SYSTEM_COLORS.borderSoft }}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} style={{ color: BRAND_COLORS.primary }} />
              <span className="text-xs" style={{ color: BRAND_COLORS.primary }}>{label}</span>
            </div>
            <p className="text-xl font-bold" style={{ color: NEUTRAL_COLORS.black }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none"
               style={{ color: SYSTEM_COLORS.textMuted }}>
          <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} />
          Active only
        </label>
        <div className="flex gap-2">
          <button onClick={load}
            className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={() => { setEditingAccount(null); setShowForm(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={15} /> Add Account
          </button>
        </div>
      </div>

      {/* Account grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-dashed"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <Users size={32} style={{ color: SYSTEM_COLORS.textSoft }} />
          <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            No social accounts yet — register your first one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map(account => (
            <AccountCard
              key={account.id}
              account={account}
              onEdit={(a) => { setEditingAccount(a); setShowForm(true); }}
              onDelete={handleDelete}
              onSnapshot={(a) => setSnapshotTarget(a)}
            />
          ))}
        </div>
      )}

      <AccountFormModal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingAccount(null); }}
        onSave={handleSave}
        initial={editingAccount}
      />

      <SnapshotModal
        isOpen={!!snapshotTarget}
        onClose={() => setSnapshotTarget(null)}
        onSave={handleSnapshot}
        accountName={snapshotTarget ? `@${snapshotTarget.handle}` : ''}
      />
    </div>
  );
};

export default SocialAccountsManager;
